import "./Receipt.css";

import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../../services/api";
import { useNotification } from "../../context/NotificationContext";

const VAT_RATE = 0.18;

const formatMoney = (value) =>
  Number(value).toLocaleString("he-IL", {
    style: "currency",
    currency: "ILS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const translateDelivery = (value) =>
  ({
    delivery: "משלוח",
    pickup: "איסוף עצמי",
  })[value] ||
  value ||
  "-";

const translatePayment = (value) =>
  ({
    cash: "מזומן",
    visa: "כרטיס אשראי",
    paypal: "PayPal",
  })[value] ||
  value ||
  "-";

const translateStatus = (value) =>
  ({
    Pending: "התקבלה",
    Paid: "התשלום אושר",
    Preparing: "בהכנה",
    Shipped: "במשלוח",
    Delivered: "נמסרה",
    Cancelled: "בוטלה",
  })[value] ||
  value ||
  "-";

function Receipt() {
  const { showNotification } = useNotification();
  const [params] = useSearchParams();
  const orderId = params.get("order");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    setOrder(null);
    setError("");

    if (!orderId) {
      setError("לא נמצא מספר הזמנה");
      setLoading(false);
      return;
    }

    const token = localStorage.getItem("token");

    if (!token) {
      setError("יש להתחבר כדי לצפות בקבלה");
      setLoading(false);
      return;
    }

    setLoading(true);

    api
      .get(`/orders/${encodeURIComponent(orderId)}/details`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((res) => {
        if (cancelled) return;

        const total = Number(res.data?.total_price);

        if (
          res.data?.total_price == null ||
          res.data.total_price === "" ||
          !Number.isFinite(total) ||
          total < 0
        ) {
          throw new Error("Invalid order total");
        }

        setOrder(res.data);
      })
      .catch((err) => {
        if (cancelled) return;

        console.error("Receipt loading failed:", err);
        setError("טעינת הקבלה נכשלה");
        showNotification("טעינת הקבלה נכשלה", "error");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [orderId, showNotification]);

  if (loading) {
    return (
      <main className="receipt-page" dir="rtl" lang="he">
        <p role="status">טוען קבלה...</p>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="receipt-page" dir="rtl" lang="he">
        <section className="receipt-card">
          <p role="alert">{error || "הקבלה אינה זמינה כרגע"}</p>

          <div className="receipt-actions no-print">
            <Link to="/profile">חזרה לפרופיל</Link>
            <Link to="/login">התחברות</Link>
          </div>
        </section>
      </main>
    );
  }

  // חלוקת הסכום הקיים למחיר לפני מע״מ ולמע״מ הכלול בו.
  // החישוב באגורות שומר על התאמה בין סכומי התצוגה.
  const totalAgorot = Math.round(Number(order.total_price) * 100);
  const beforeVatAgorot = Math.round(totalAgorot / (1 + VAT_RATE));
  const vatAgorot = totalAgorot - beforeVatAgorot;

  const orderDate = order.order_date ? new Date(order.order_date) : null;

  const formattedDate =
    orderDate && !Number.isNaN(orderDate.getTime())
      ? orderDate.toLocaleDateString("he-IL")
      : "-";

  return (
    <main className="receipt-page" dir="rtl" lang="he">
      <div className="receipt-actions no-print">
        <Link to="/profile">חזרה לפרופיל</Link>

        <button type="button" onClick={() => window.print()}>
          הדפסה / שמירה כ-PDF
        </button>
      </div>

      <section className="receipt-card">
        <header className="receipt-header">
          <div>
            <h1>Cervica</h1>
            <p>קבלה עבור הזמנה #{order.order_id}</p>
          </div>

          <div className="receipt-meta">
            <span>תאריך</span>
            <strong>{formattedDate}</strong>
          </div>
        </header>

        <div className="receipt-status">
          <span>סטטוס הזמנה</span>
          <strong>{translateStatus(order.status)}</strong>
        </div>

        <section className="receipt-grid">
          <div>
            <span>שם לקוח</span>
            <strong>
              {[order.first_name, order.last_name].filter(Boolean).join(" ") ||
                "-"}
            </strong>
          </div>

          <div>
            <span>טלפון</span>
            <strong>
              <bdi>{order.phone || "-"}</bdi>
            </strong>
          </div>

          <div>
            <span>אימייל</span>
            <strong>
              <bdi>{order.email || "-"}</bdi>
            </strong>
          </div>

          <div>
            <span>עיר</span>
            <strong>{order.city || "-"}</strong>
          </div>

          <div>
            <span>כתובת</span>
            <strong>{order.delivery_address || "-"}</strong>
          </div>

          <div>
            <span>מיקוד</span>
            <strong>{order.postal_code || "-"}</strong>
          </div>

          <div>
            <span>שיטת קבלה</span>
            <strong>{translateDelivery(order.delivery_method)}</strong>
          </div>

          <div>
            <span>שיטת תשלום</span>
            <strong>{translatePayment(order.payment_method)}</strong>
          </div>
        </section>

        <section className="receipt-items">
          <h2>מוצרים בהזמנה</h2>

          <table>
            <thead>
              <tr>
                <th scope="col">מוצר</th>
                <th scope="col">כמות</th>
                <th scope="col">מחיר כולל מע״מ</th>
                <th scope="col">סה״כ כולל מע״מ</th>
              </tr>
            </thead>

            <tbody>
              {order.items?.map((item) => (
                <tr key={item.order_item_id}>
                  <td>{item.product_name}</td>
                  <td>{item.quantity}</td>
                  <td>
                    <bdi>{formatMoney(item.price)}</bdi>
                  </td>
                  <td>
                    <bdi>
                      {formatMoney(Number(item.price) * Number(item.quantity))}
                    </bdi>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <div className="receipt-vat-details">
          <div className="receipt-vat-row">
            <span>סכום לפני מע״מ</span>
            <strong>
              <bdi>{formatMoney(beforeVatAgorot / 100)}</bdi>
            </strong>
          </div>

          <div className="receipt-vat-row">
            <span>מע״מ (18%)</span>
            <strong>
              <bdi>{formatMoney(vatAgorot / 100)}</bdi>
            </strong>
          </div>
        </div>

        <footer className="receipt-total">
          <span>סה״כ כולל מע״מ</span>
          <strong>
            <bdi>{formatMoney(totalAgorot / 100)}</bdi>
          </strong>
        </footer>

        <p className="receipt-vat-note">המחיר כולל מע״מ בשיעור 18%.</p>

        <p className="receipt-note">
          תודה שבחרתם Cervica. הקבלה נוצרה באופן אוטומטי ממערכת ההזמנות.
        </p>
      </section>
    </main>
  );
}

export default Receipt;
